import React, { useEffect, useState } from 'react';
import { Instagram, Play, Heart, MessageCircle } from 'lucide-react';
import { BEHOLD_FEED_URL, INSTAGRAM_URL } from '../config/contact';

const GOLD = '#C0975C';
const IVORY = '#F5F1E8';

const imgFor = (p) => p?.sizes?.medium?.mediaUrl || p?.sizes?.large?.mediaUrl || p?.mediaUrl;

// Live Instagram grid powered by the Behold.so JSON feed.
export function InstagramFeed({ limit = 8 }) {
  const [posts, setPosts] = useState(null); // null = loading, [] = failed/empty

  useEffect(() => {
    let alive = true;
    fetch(BEHOLD_FEED_URL)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => { if (alive) setPosts(Array.isArray(d?.posts) ? d.posts.slice(0, limit) : []); })
      .catch(() => { if (alive) setPosts([]); });
    return () => { alive = false; };
  }, [limit]);

  if (posts === null) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="ig-feed-loading">
        {Array.from({ length: limit }).map((_, i) => (
          <div key={i} className="aspect-square rounded-xl animate-pulse" style={{ backgroundColor: '#122C5A' }} />
        ))}
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="text-center py-10" data-testid="ig-feed-empty">
        <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold" style={{ backgroundColor: GOLD, color: '#0A1F44' }}>
          <Instagram className="w-4 h-4" /> View our latest on Instagram
        </a>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="ig-feed-grid">
      {posts.map((p) => (
        <a
          key={p.id}
          href={p.permalink}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="ig-post"
          className="group relative aspect-square rounded-xl overflow-hidden block"
          style={{ backgroundColor: '#122C5A', border: '1px solid #1D3D72' }}
        >
          <img
            src={imgFor(p)}
            alt={p.prunedCaption ? p.prunedCaption.slice(0, 80) : 'Precision Wealth Instagram post'}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          {(p.mediaType === 'VIDEO' || p.mediaType === 'CAROUSEL_ALBUM') && (
            <div className="absolute top-2.5 right-2.5 text-white drop-shadow">
              {p.mediaType === 'VIDEO'
                ? <Play className="w-4 h-4 fill-white" />
                : <span className="block w-3.5 h-3.5 rounded-sm border-2 border-white" />}
            </div>
          )}
          <div className="absolute inset-0 flex flex-col justify-end p-3 opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ background: 'linear-gradient(180deg, rgba(10,31,68,0) 40%, rgba(6,21,48,0.92) 100%)' }}>
            <p className="text-[11px] leading-snug line-clamp-3 mb-2" style={{ color: IVORY }}>{p.prunedCaption || ''}</p>
            <div className="flex items-center gap-3 text-[11px]" style={{ color: GOLD }}>
              <span className="inline-flex items-center gap-1"><Heart className="w-3.5 h-3.5" /> {p.likeCount ?? 0}</span>
              <span className="inline-flex items-center gap-1"><MessageCircle className="w-3.5 h-3.5" /> {p.commentsCount ?? 0}</span>
              <Instagram className="w-3.5 h-3.5 ml-auto" />
            </div>
          </div>
        </a>
      ))}
    </div>
  );
}

export default InstagramFeed;
